/**
 * Markdown rendering for agent answers.
 *
 * The agent writes markdown — headings, tables, bullet lists, `inline code`,
 * **bold** — and the panel used to print it as one pre-wrapped string, pipes
 * and asterisks included. This turns that subset into React elements.
 *
 * Deliberately NOT a full CommonMark implementation, and no dependency: the
 * input is machine-written markdown from Claude, so the covered set is the one
 * it actually emits — headings, tables, ordered/bullet lists, fenced and inline
 * code, bold/italic/strikethrough, links, thematic breaks. Anything outside
 * that (blockquotes, nested lists, images, footnotes, HTML) degrades to plain
 * text rather than breaking; extend a case here when the agent starts using it.
 *
 * Everything becomes React elements — there is no `dangerouslySetInnerHTML` on
 * this path, so agent output cannot inject markup, and link hrefs are limited
 * to http/https/mailto so a `javascript:` target cannot survive the round trip.
 *
 * LAYOUT RULE (see containerSnapshotAgent): the tab has exactly ONE vertical
 * scroll region. Nothing here sets a max-height; wide tables and code blocks
 * scroll HORIZONTALLY inside themselves, which is the only way they can be read
 * without a second vertical scroller.
 */

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { CONTROL_BG, FIELD_LABEL, SECTION_DIVIDER } from "./panelPalette";

const useStyles = makeStyles(() => ({
  root: {
    fontSize: "13px",
    lineHeight: 1.6,
    color: "#e4e4e4",
    // The answer sits in a padded box already — no leading or trailing gap.
    "& > *:first-child": { marginTop: 0 },
    "& > *:last-child": { marginBottom: 0 }
  },

  // pre-wrap, not collapse: the agent uses single newlines as line breaks and
  // reflowing them the way CommonMark would runs its lists of facts together.
  paragraph: { whiteSpace: "pre-wrap", wordBreak: "break-word", margin: "0 0 8px" },

  heading: { fontWeight: 600, color: "#f2f2f2", lineHeight: 1.35, margin: "14px 0 6px" },
  h1: { fontSize: "15.5px" },
  h2: { fontSize: "14.5px" },
  h3: { fontSize: "13.5px" },
  h4: { fontSize: "13px", color: FIELD_LABEL },

  list: { margin: "0 0 8px", paddingLeft: "18px" },
  listItem: { margin: "2px 0", wordBreak: "break-word" },

  hr: { border: 0, borderTop: `1px solid ${SECTION_DIVIDER}`, margin: "12px 0" },

  code: {
    fontFamily: "monospace",
    fontSize: "11.5px",
    backgroundColor: CONTROL_BG,
    borderRadius: "3px",
    padding: "1px 4px",
    wordBreak: "break-word"
  },
  pre: {
    backgroundColor: CONTROL_BG,
    borderRadius: "4px",
    padding: "8px 10px",
    margin: "0 0 8px",
    overflowX: "auto",
    fontFamily: "monospace",
    fontSize: "11.5px",
    lineHeight: 1.55,
    color: "#cfcfcf"
  },

  tableWrap: { overflowX: "auto", margin: "0 0 8px" },
  table: { borderCollapse: "collapse", fontSize: "12px", minWidth: "100%" },
  th: {
    border: `1px solid ${SECTION_DIVIDER}`,
    backgroundColor: CONTROL_BG,
    color: FIELD_LABEL,
    fontWeight: 600,
    padding: "4px 8px",
    whiteSpace: "nowrap"
  },
  td: {
    border: `1px solid ${SECTION_DIVIDER}`,
    padding: "4px 8px",
    verticalAlign: "top",
    wordBreak: "break-word"
  },

  link: { color: "#7e9cd8", textDecoration: "none", "&:hover": { textDecoration: "underline" } }
}));

// ── Block scanning ─────────────────────────────────────────────────────────

const FENCE_RE = /^\s{0,3}(```|~~~)/;
const HEADING_RE = /^\s{0,3}(#{1,6})\s+(.*)$/;
const HR_RE = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
const ITEM_RE = /^\s*(?:[-*+]|\d+[.)])\s+(.*)$/;
const ORDERED_ITEM_RE = /^\s*\d+[.)]\s+/;

/** A `|---|:--:|` row — what separates a table's header from its body. */
function isTableSeparator(line) {
  if (!line || !line.includes("|") || !line.includes("-")) return false;
  return /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(line) && /-/.test(line);
}

function splitRow(line) {
  const cells = line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|");
  return cells.map((cell) => cell.trim());
}

function alignOf(spec) {
  const left = spec.startsWith(":");
  const right = spec.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return undefined;
}

/** Does a NEW block start at lines[i]? Used to stop a paragraph. */
function startsBlock(lines, i) {
  const line = lines[i];
  if (FENCE_RE.test(line) || HEADING_RE.test(line) || HR_RE.test(line)) return true;
  if (ITEM_RE.test(line)) return true;
  return line.includes("|") && isTableSeparator(lines[i + 1]);
}

function parseBlocks(text) {
  const lines = String(text).replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i += 1;
      continue;
    }

    // Fenced code — taken verbatim, so a fence full of pipes or hashes cannot
    // be mistaken for a table or a heading.
    const fence = line.match(FENCE_RE);
    if (fence) {
      const marker = fence[1];
      const body = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith(marker)) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // the closing fence, or past the end if it was never closed
      blocks.push({ type: "code", text: body.join("\n") });
      continue;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      blocks.push({
        type: "heading",
        level: heading[1].length,
        text: heading[2].replace(/\s+#+\s*$/, "")
      });
      i += 1;
      continue;
    }

    if (HR_RE.test(line)) {
      blocks.push({ type: "hr" });
      i += 1;
      continue;
    }

    if (line.includes("|") && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line);
      const align = splitRow(lines[i + 1]).map(alignOf);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes("|")) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push({ type: "table", header, align, rows });
      continue;
    }

    if (ITEM_RE.test(line)) {
      const ordered = ORDERED_ITEM_RE.test(line);
      const items = [];
      while (i < lines.length && lines[i].trim()) {
        const item = lines[i].match(ITEM_RE);
        if (item) items.push(item[1]);
        else if (items.length) items[items.length - 1] += ` ${lines[i].trim()}`;
        else break;
        i += 1;
      }
      blocks.push({ type: "list", ordered, items });
      continue;
    }

    // Paragraph. The first line is consumed unconditionally, which is what
    // guarantees this loop always advances.
    const paragraph = [line.trim()];
    i += 1;
    while (i < lines.length && lines[i].trim() && !startsBlock(lines, i)) {
      paragraph.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: "paragraph", text: paragraph.join("\n") });
  }

  return blocks;
}

// ── Inline spans ───────────────────────────────────────────────────────────

// One pass, alternation ordered by precedence: code first so that markup
// inside a code span stays literal, then links, then emphasis.
// `(\S|\S[\s\S]*?\S)` — an emphasis run may not begin or end on whitespace.
// Without that, the asterisks in "2 * 3 * 4" read as an italic " 3 ".
const RUN = "(\\S|\\S[\\s\\S]*?\\S)";
const LINE_RUN = (char) => `(\\S|\\S[^${char}\\n]*?\\S)`;

const INLINE_SOURCE = [
  "(`+)([\\s\\S]*?)\\1", // 1,2 code span
  "\\[([^\\]]*)\\]\\(([^)\\s]+)\\)", // 3,4 link
  `\\*\\*${RUN}\\*\\*`, // 5 bold
  `__${RUN}__`, // 6 bold
  `~~${RUN}~~`, // 7 strikethrough
  `\\*${LINE_RUN("*")}\\*`, // 8 italic
  `_${LINE_RUN("_")}_`, // 9 italic
  "(https?://[^\\s<>\"')\\]]+)" // 10 bare url
].join("|");

/**
 * A FRESH regex per call — never a shared module-level one.
 *
 * renderInline recurses (the text inside **bold** is parsed again), and a `/g`
 * regex carries `lastIndex` on the object itself. Sharing one meant the inner
 * call drained it and left it at 0, so the outer loop restarted from the top
 * and re-matched the same span forever: any answer containing bold, a link or
 * italics locked the browser tab solid.
 */
function inlineMatcher() {
  return new RegExp(INLINE_SOURCE, "g");
}

/**
 * Is this `_` run actually part of a longer identifier?
 *
 * `_` is the one marker that turns up inside names the agent quotes all the
 * time — `..._mip_20260818_05_emily_game_scratch_...` would otherwise come out
 * half-italic. CommonMark settles this with flanking rules; the character on
 * either side of the run decides it here, which needs no lookbehind (Safari
 * only got those in 16.4).
 */
function intraword(text, match) {
  const before = match.index > 0 ? text[match.index - 1] : "";
  const after = text[match.index + match[0].length] || "";
  return /\w/.test(before) || /\w/.test(after);
}

/** Only schemes that cannot execute; anything else renders as plain text. */
function safeHref(url) {
  return /^(?:https?:|mailto:)/i.test(url) ? url : null;
}

function renderInline(text, classes, keyPrefix) {
  const nodes = [];
  let last = 0;
  let seq = 0;
  let match;

  const matcher = inlineMatcher();
  while ((match = matcher.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${seq}`;
    seq += 1;

    if (match[2] !== undefined) {
      nodes.push(
        <code key={key} className={classes.code}>
          {match[2]}
        </code>
      );
    } else if (match[4] !== undefined) {
      const href = safeHref(match[4]);
      nodes.push(
        href ? (
          <a key={key} className={classes.link} href={href} target="_blank" rel="noopener noreferrer">
            {renderInline(match[3], classes, key)}
          </a>
        ) : (
          match[0]
        )
      );
    } else if (match[5] !== undefined) {
      nodes.push(<strong key={key}>{renderInline(match[5], classes, key)}</strong>);
    } else if (match[6] !== undefined) {
      nodes.push(
        intraword(text, match) ? (
          match[0]
        ) : (
          <strong key={key}>{renderInline(match[6], classes, key)}</strong>
        )
      );
    } else if (match[7] !== undefined) {
      nodes.push(
        <span key={key} style={{ textDecoration: "line-through", opacity: 0.7 }}>
          {renderInline(match[7], classes, key)}
        </span>
      );
    } else if (match[8] !== undefined) {
      nodes.push(<em key={key}>{renderInline(match[8], classes, key)}</em>);
    } else if (match[9] !== undefined) {
      nodes.push(
        intraword(text, match) ? (
          match[0]
        ) : (
          <em key={key}>{renderInline(match[9], classes, key)}</em>
        )
      );
    } else if (match[10] !== undefined) {
      nodes.push(
        <a key={key} className={classes.link} href={match[10]} target="_blank" rel="noopener noreferrer">
          {match[10]}
        </a>
      );
    }

    last = match.index + match[0].length;
  }

  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// ── Rendering ──────────────────────────────────────────────────────────────

function renderBlock(block, index, classes) {
  const key = `b${index}`;

  switch (block.type) {
  case "heading":
    return React.createElement(
      `h${Math.min(block.level, 6)}`,
      {
        key,
        className: `${classes.heading} ${classes[`h${Math.min(block.level, 4)}`] || classes.h4}`
      },
      renderInline(block.text, classes, key)
    );

  case "code":
    return (
      <pre key={key} className={classes.pre}>
        {block.text}
      </pre>
    );

  case "hr":
    return <hr key={key} className={classes.hr} />;

  case "list": {
    const Tag = block.ordered ? "ol" : "ul";
    return (
      <Tag key={key} className={classes.list}>
        {block.items.map((item, itemIndex) => (
          <li key={`${key}-${itemIndex}`} className={classes.listItem}>
            {renderInline(item, classes, `${key}-${itemIndex}`)}
          </li>
        ))}
      </Tag>
    );
  }

  case "table":
    return (
      <div key={key} className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              {block.header.map((cell, cellIndex) => (
                <th
                  key={`${key}-h${cellIndex}`}
                  className={classes.th}
                  style={{ textAlign: block.align[cellIndex] }}
                >
                  {renderInline(cell, classes, `${key}-h${cellIndex}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={`${key}-r${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={`${key}-r${rowIndex}c${cellIndex}`}
                    className={classes.td}
                    style={{ textAlign: block.align[cellIndex] }}
                  >
                    {renderInline(cell, classes, `${key}-r${rowIndex}c${cellIndex}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  default:
    return (
      <div key={key} className={classes.paragraph}>
        {renderInline(block.text, classes, key)}
      </div>
    );
  }
}

export default function AgentMarkdown({ text, className }) {
  const classes = useStyles();

  // The ELEMENTS are memoised, not just the block scan.
  //
  // This panel re-renders on every socket message, and a running agent sends
  // one per step. The server rebuilds the whole session each time, so every
  // turn object arrives with a fresh identity and React re-renders all of them
  // — re-parsing every answer and, because renderInline recurses with a fresh
  // RegExp per call, allocating thousands of short-lived objects each time. A
  // profile put 58% of the page's time in cycle collection, not in this code:
  // the cost was the garbage, not the work.
  //
  // `text` is a string, so the dependency compares by VALUE — the memo survives
  // the new turn objects and the parse happens once per distinct answer.
  const content = useMemo(
    () => parseBlocks(text || "").map((block, index) => renderBlock(block, index, classes)),
    [text, classes]
  );

  return (
    <div className={className ? `${classes.root} ${className}` : classes.root}>{content}</div>
  );
}

AgentMarkdown.propTypes = {
  /** Raw markdown, as the agent wrote it. */
  text: PropTypes.string,
  className: PropTypes.string
};

// Exported for the unit tests. Both halves need pinning down: the block scanner
// decides what a table is, and renderInline is where a shared regex once looped
// forever — see inlineMatcher.
export { parseBlocks, renderInline };
